// React and Library Imports
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import moment from "moment";
import Drawer from "components/ui-component/SideDrawer";
import Skeleton from "react-loading-skeleton";
import { Menu } from "@headlessui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

// Store Hooks and Slices
import { useAppDispatch } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import { useLeadData, usePortalVanityUrl } from "store/slices/dashboardSlice";
import {
  paymentReceiptDownload,
  paymentReceiptDownloadPdf,
  paymentScheduledGet,
  receiptIdGet,
  useScheduleHistory,
  useScheduleHistoryCount,
} from "store/slices/paymentsSlice";

// Components
import SelectAllDropDown from "components/form/SelectAllDropDown";
import CustomTable from "components/ui-component/CustomTable";
import Button from "components/form/Button";
import AmountFilter from "./AmountFilter";
import PaymentPreview from "./PaymentPreview";
import DatePickerFilter from "components/ui-component/DatePickerFilter";

// Icons
import { DollarProcessIcon } from "icons/payments/DollarProcessIcon";
import { MoneyReciveIcon } from "icons/payments/MoneyReciveIcon";
import { CloseYellowIcon } from "icons/payments/CloseYellowIcon";
import { GreenTrueIcon } from "icons/payments/GreenTrueIcon";
import { RedCrossIcon } from "icons/payments/RedCrossIcon";
import { DownloadIcon } from "icons/payments/DownloadIcon";
import { InfoIcon } from "icons/payments/InfoIcon";
import { MoreIcon } from "icons/payments/MoreIcon";
import { EyeIcon } from "icons/payments/EyeIcon";
import { ClockYellowIcon } from "icons/payments/ClockYellowIcon";
import NoPayments from "icons/payments/NoPayments";

// Utils and Constants
import {
  cardToShortname,
  encode,
  formatCurrency,
  getPaymentMethod,
  decode,
  downloadPdfFromApi,
} from "utils/HelperFunctions";
import { dateToString, formatPaymentListDate } from "utils/DateFormat";
import {
  FilterAmountOptions,
  PaymentFilterMethodOptions,
  scheduledFilterTimeRanges,
  scheduledTimeRanges,
} from "utils/Options";
import { DateState, PaymentFilterTypes } from "types/common";
import { ScheduledColumns, ScheduledRow } from "types/scheduled";
import { useTid } from "utils/testid";
import { onActivate } from "utils/a11y";

/* ------------------------------------- PAYMNET TAB ------------------------------------- */

const ScheduledTab = () => {
  const tid = useTid();
  const vanityUrl = usePortalVanityUrl();
  // Store and navigation hooks
  const dispatch = useAppDispatch();
  const userData = useUserData();
  const filterScrollRef = useRef<HTMLDivElement>(null);
  const amountFilterRef = useRef<HTMLDivElement>(null);
  const dateFilterRef = useRef<HTMLDivElement>(null);
  const methodFilterRef = useRef<HTMLDivElement>(null);

  const scheduleHistory = useScheduleHistory();
  const scheduleHistoryCount = useScheduleHistoryCount();
  const navigate = useNavigate();
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const accountLeadData = useLeadData();
  const isLessThan590 = useMediaQuery({ query: "(max-width: 590px)" });

  // UI and filter state
  const [selectedFilter, setSelectedFilter] = useState<
    "all" | "currentMonthCount" | "nextMonthCount" | "next3Month"
  >("all");
  const [method, setMethod] = useState<string[]>([]);
  const [amount, setAmount] = useState<any>();
  const [openCal, setOpenCal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [isPreviewId, setIsPreviewId] = useState<string>("");
  const [isCustomSchPreviewId, setIsCustomSchPreviewId] = useState<string>("");
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState("");
  const [filter, setFilter] = useState<PaymentFilterTypes>({
    offset: 0,
    limit: 20,
    method: undefined,
    status: undefined,
    amount: undefined,
    startDate: undefined,
    endDate: undefined,
  });
  const [dateState, setDateState] = useState<DateState>({
    start: "",
    end: "",
  });

  // Date picker handlers
  const handleCallback = useCallback((start: any, end: any): void => {
    if (filterScrollRef.current && dateFilterRef.current) {
      filterScrollRef.current.scrollTo({
        left:
          dateFilterRef.current.offsetLeft -
          (filterScrollRef.current.offsetLeft || 0),
        behavior: "smooth",
      });
    }
    setTimeout(() => {
      setOpenCal(true);
      setDateState({ start, end });
    }, 250);
    setTimeout(() => {
      ensureActiveBlue();
      installRangesObserver();
    }, 300);
  }, []);

  const label = useMemo(
    () =>
      dateState?.start || dateState?.end
        ? `${moment(dateState?.start)?.format("MMM D, YYYY")} - ${moment(
            dateState?.end
          )?.format("MMM D, YYYY")}`
        : null,
    [dateState]
  );

  // Utility: highlight active date range in blue
  const ensureActiveBlue = useCallback((): void => {
    const dropdown = document.querySelector(".daterangepicker");
    const allLis = dropdown?.querySelectorAll(
      ".ranges li"
    ) as NodeListOf<HTMLElement> | null;
    const allAnchors = dropdown?.querySelectorAll(
      ".ranges li a"
    ) as NodeListOf<HTMLElement> | null;
    allLis?.forEach((el) => el.style.removeProperty("color"));
    allAnchors?.forEach((el) => el.style.removeProperty("color"));
    const activeLi = dropdown?.querySelector(
      ".ranges li.active"
    ) as HTMLElement | null;
    if (activeLi)
      activeLi.style.setProperty("color", "var(--color-blue)", "important");
    const activeAnchor = dropdown?.querySelector(
      ".ranges li.active a, .ranges li > a.active"
    ) as HTMLElement | null;
    if (activeAnchor)
      activeAnchor.style.setProperty("color", "var(--color-blue)", "important");
  }, []);

  // Utility: observe range changes for blue highlight
  const installRangesObserver = useCallback((): void => {
    const dropdown = document.querySelector(".daterangepicker");
    const ranges = dropdown?.querySelector(".ranges");
    if (!ranges) return;
    const observer = new MutationObserver(() => {
      ensureActiveBlue();
    });
    observer.observe(ranges, {
      attributes: true,
      subtree: true,
      attributeFilter: ["class"],
    });
    setTimeout(() => observer.disconnect(), 2000);
  }, [ensureActiveBlue]);

  // Date picker show/hide handlers
  const handleShow = useCallback((): void => {
    if (!dateState?.start && !dateState?.end) {
      const dropdown = document.querySelector(".daterangepicker");
      const active = dropdown?.querySelector(".ranges li.active");
      if (active) active.classList.remove("active");
    }
    setTimeout(() => {
      const dropdown = document.querySelector(".daterangepicker");
      dropdown?.classList.add("scheduled-drp");
      ensureActiveBlue();
      installRangesObserver();
    }, 0);
  }, [dateState, ensureActiveBlue, installRangesObserver]);

  const handleHide = useCallback((): void => {
    const dropdown = document.querySelector(".daterangepicker");
    dropdown?.classList.remove("scheduled-drp");
  }, []);

  // Data fetching handlers
  const resolveReceiptId = useCallback(
    async (
      orderId: string | number,
      fallbackReceiptId?: string | number | null
    ): Promise<string | number> => {
      try {
        const response: any = await dispatch(
          receiptIdGet({ orders_id: orderId })
        ).unwrap();
        const receiptId = response?.body?.data?.id;

        if (response?.body?.success && receiptId) {
          return receiptId;
        }
      } catch (error: any) {
        console.log("get receipt id err: -->", error?.message);
      }

      return fallbackReceiptId || orderId;
    },
    [dispatch]
  );

  const onPaymentDownload = useCallback(
    async (
      id: string | number,
      receiptId?: string | number | null,
      shouldResolveReceiptId = false
    ): Promise<void> => {
      const downloadKey = String(id);
      setIsDownloading(downloadKey);
      let pid = encode(`${id}_${clusterId}_${new Date().getTime()}`, "pid_");
      try {
        const resolvedReceiptId = shouldResolveReceiptId
          ? await resolveReceiptId(id, receiptId)
          : receiptId || id;
        let payload = {
          invoice_id: id,
          lead_id: accountLeadData?.lead_id,
          url: `${vanityUrl}view-payment/${pid}`,
          cluster_id: clusterId,
          isFromPortal: true,
        };
        await downloadPdfFromApi(
          () => dispatch(paymentReceiptDownloadPdf(payload)).unwrap(),
          `Wire transfer request - ${resolvedReceiptId}.pdf`,
          (loading) =>
            setIsDownloading((current) =>
              loading ? downloadKey : current === downloadKey ? "" : current
            )
        );
      } catch (error: any) {
        console.log("payment download err: -->", error?.message);
      } finally {
        setIsDownloading((current) => (current === downloadKey ? "" : current));
      }
    },
    [accountLeadData, clusterId, dispatch, resolveReceiptId]
  );

  const onGetScheduledHistory = useCallback(async (): Promise<void> => {
    setIsTableLoading(true);
    setIsLoading(true);
    try {
      let payload = {
        ...filter,
        accounts_id: userData?.data?.accountData?.id,
      };
      await dispatch(paymentScheduledGet(payload)).unwrap();
    } catch (error: any) {
      console.log("get payment history err: -->", error?.message);
    } finally {
      setIsTableLoading(false);
      setIsLoading(false);
    }
  }, [dispatch, filter, userData]);

  useEffect(() => {
    onGetScheduledHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const selectedSlot = useMemo(() => {
    if (dateState?.start && dateState?.end) {
      if (
        dateState?.start === scheduledFilterTimeRanges["This Month"]?.[0] &&
        dateState?.end === scheduledFilterTimeRanges["This Month"]?.[1]
      ) {
        return "currentMonthCount";
      } else if (
        dateState?.start === scheduledFilterTimeRanges["Next Month"]?.[0] &&
        dateState?.end === scheduledFilterTimeRanges["Next Month"]?.[1]
      ) {
        return "nextMonthCount";
      } else if (
        dateState?.start === scheduledFilterTimeRanges["Next 3 Months"]?.[0] &&
        dateState?.end === scheduledFilterTimeRanges["Next 3 Months"]?.[1]
      ) {
        return "next3Month";
      }
      return "";
    } else {
      return "all";
    }
  }, [selectedFilter, dateState]);

  useEffect(() => {
    if (selectedFilter === "all" && !dateState?.start && !dateState?.end)
      return;
    switch (selectedFilter) {
      case "currentMonthCount":
        setDateState({
          start: scheduledFilterTimeRanges["This Month"]?.[0],
          end: scheduledFilterTimeRanges["This Month"]?.[1],
        });
        break;
      case "nextMonthCount":
        setDateState({
          start: scheduledFilterTimeRanges["Next Month"]?.[0],
          end: scheduledFilterTimeRanges["Next Month"]?.[1],
        });
        break;
      case "next3Month":
        setDateState({
          start: scheduledFilterTimeRanges["Next 3 Months"]?.[0],
          end: scheduledFilterTimeRanges["Next 3 Months"]?.[1],
        });
        break;
      default:
        setDateState({
          start: "",
          end: "",
        });
        setOpenCal(false);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  useEffect(() => {
    const methodFormatted = method?.map((data) => cardToShortname(data));
    // Keep the same object when nothing actually changed, so the fetch effect
    // doesn't re-run on mount (React bails out when the updater returns the
    // previous state).
    setFilter((prev: any) => {
      const next = {
        ...prev,
        offset: 0,
        method: method.length > 0 ? JSON.stringify(methodFormatted) : undefined,
        amount: amount ? JSON.stringify(amount) : undefined,
        startDate: dateState?.start
          ? dateToString(dateState?.start)
          : undefined,
        endDate: dateState?.end ? dateToString(dateState?.end) : undefined,
      };
      const unchanged = Object.keys(next).every(
        (key) => next[key] === prev[key]
      );
      return unchanged ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method, dateState, amount]);

  const columns: ScheduledColumns[] = [
    {
      id: "billing_date",
      name: "Payment Date",
      minWidth: "130px",
      cell: (row: ScheduledRow) => {
        return (
          <div>
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.billing_date ? (
              formatPaymentListDate(
                row?.billing_date,
                userData?.data?.accountData?.timezone,
                "short"
              )
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "amount",
      name: "Amount",
      minWidth: "130px",
      cell: (row: ScheduledRow) => {
        return (
          <div className="text-xl font-medium text-txtPrimary leading-normal">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.amount ? (
              formatCurrency(row?.amount + row?.cf_amount) || "$0.00"
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "cc_type",
      name: "Payment Method",
      minWidth: "150px",
      cell: (row: ScheduledRow) => {
        return (
          <div className="w-full text-left">
            {isTableLoading ? (
              <Skeleton width={120} />
            ) : row?.payment_method ? (
              getPaymentMethod({
                card_type: row?.cc_type,
                method: row?.payment_method,
                digits: row?.cc_4digit,
                account_number: row?.cc_4digit,
                isHidden: false,
                bank_name: row?.bank_name,
                rowId: row?.id,
              })
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "status",
      name: "Status",
      minWidth: "200px",
      cell: (row: ScheduledRow) => {
        let status = row?.status ? row?.status?.toLowerCase() : null;
        return (
          <div className="pointer-events-none">
            {isTableLoading ? (
              <Skeleton width={150} />
            ) : status ? (
              <div
                className={`flex items-center py-[3px] px-[10px] gap-[6px] rounded-md ${
                  status === "succeeded"
                    ? "bg-[#23B3390D] text-green text-sm"
                    : status === "failed"
                      ? "bg-[#FF00000D] text-red text-sm"
                      : status === "refunded"
                        ? "bg-[#1678F20D] text-blue text-sm"
                        : status === "voided" || status === "pending"
                          ? "bg-[#FDB6000D] text-yellow text-sm"
                          : status === "partial refund"
                            ? "bg-[#F3F8FE] text-blue text-sm"
                            : status === "disputed"
                              ? "bg-[#4F576A0D] text-green text-sm"
                              : ""
                }`}
              >
                {status === "succeeded" ? (
                  <GreenTrueIcon />
                ) : status === "failed" ? (
                  <RedCrossIcon />
                ) : status === "refunded" ? (
                  <MoneyReciveIcon />
                ) : status === "voided" ? (
                  <CloseYellowIcon />
                ) : status === "partial refund" ? (
                  <DollarProcessIcon />
                ) : status === "disputed" ? (
                  <InfoIcon />
                ) : status === "pending" ? (
                  <ClockYellowIcon />
                ) : (
                  ""
                )}
                {row?.status}
              </div>
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "payment_description",
      name: "Payment Type",
      minWidth: "150px",
      cell: (row: ScheduledRow) => {
        let description = row?.description ? row?.description : null;
        return (
          <div className="pointer-events-none">
            {isTableLoading ? (
              <Skeleton width={150} />
            ) : description ? (
              <div className="text-sm font-normal leading-[18.23px] text-txtPrimary">
                {description || "--"}
              </div>
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "id",
      name: "",
      right: "true",
      minWidth: "120px",
      cell: (row: ScheduledRow) => {
        const id = `${row?.id || ""}`;
        const custom_id = `${row?.custom_schedule_id || ""}`;
        return isTableLoading ? (
          <></>
        ) : (
          <div className="flex items-center gap-5 group">
            <div className="items-center gap-5 hidden icon-group">
              {/* <div
                role="button"
                tabIndex={0}
                aria-label={`Download scheduled payment ${id}`}
                data-testid={tid(`scheduled-row-${id}-download-button`)}
                onClick={() => onPaymentDownload(id)}
                onKeyDown={onActivate(() => onPaymentDownload(id))}
              >
                {isDownloading === id ? (
                  <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                ) : (
                  <DownloadIcon
                    className="group"
                    _className="group-hover:!stroke-iconObscure"
                  />
                )}
              </div> */}
              <div
                role="button"
                tabIndex={0}
                aria-label={`Preview scheduled payment ${custom_id || id}`}
                data-testid={tid(
                  `scheduled-row-${custom_id || id}-preview-button`
                )}
                onClick={() => {
                  setIsPreview(true);
                  setIsCustomSchPreviewId(id);
                  setIsPreviewId(custom_id);
                }}
                onKeyDown={onActivate(() => {
                  setIsPreview(true);
                  setIsCustomSchPreviewId(id);
                  setIsPreviewId(custom_id);
                })}
              >
                <EyeIcon
                  className="group"
                  _className="group-hover:!fill-iconObscure"
                />
              </div>
            </div>
            <Menu as="div" className="relative">
              <Menu.Button
                aria-label={`Open actions for scheduled payment ${id}`}
                data-testid={tid(`scheduled-row-${id}-actions-button`)}
              >
                <div className="hover:!bg-bgHover p-[9px] rounded-full">
                  <MoreIcon
                    className="group"
                    _className="group-hover:!fill-iconObscure"
                  />
                </div>
              </Menu.Button>

              <Menu.Items className="absolute w-[153px] -left-[130px] top-[35px] !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg">
                <Menu.Item
                  as="div"
                  data-testid={tid(
                    `scheduled-row-${custom_id || id}-preview-menu-item`
                  )}
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-t-[7px]"
                  onClick={() => {
                    setIsPreview(true);
                    setIsCustomSchPreviewId(id);
                    setIsPreviewId(custom_id);
                  }}
                >
                  <EyeIcon width="16" height="16" /> Preview
                </Menu.Item>
                {/* <Menu.Item
                  as="div"
                  data-testid={tid(`scheduled-row-${id}-download-menu-item`)}
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-b-[7px]"
                  onClick={() => onPaymentDownload(id)}
                >
                  {isDownloading === id ? (
                    <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                  ) : (
                    <DownloadIcon width="16" height="16" />
                  )}{" "}
                  {isDownloading === id ? "Downloading..." : "Download"}
                </Menu.Item> */}
              </Menu.Items>
            </Menu>
          </div>
        );
      },
    },
  ];

  // Only claim there are no scheduled payments when the list agrees with the
  // counter, so an under-reporting counter can't hide real records.
  if (
    scheduleHistoryCount?.AllMonthCount === 0 &&
    (scheduleHistory?.totalCount || 0) === 0
  ) {
    return (
      <div className="text-center h-full grid place-items-center lg:pb-[43px] lg:px-0 px-5">
        <div className="h-fit w-fit flex items-center flex-col justify-center gap-10">
          <NoPayments
            width={window.innerWidth < 500 ? 80 : 100}
            height={window.innerWidth < 500 ? 80 : 100}
          />
          <div>
            <p className="lg:text-2xl text-xl text-txtPrimary font-semibold mb-4">
              No scheduled payments found
            </p>
            <p className="lg:text-lg text-base text-txtParagraph">
              The schedule looks clear for now. Time to set up a new payment
              plan?
            </p>
          </div>
          <Button
            text="Set up new plan"
            data-testid={tid("empty-set-up-plan-button")}
            aria-label="Set up new payment plan"
            className="lg:!px-11 lg:!py-[10px] !px-4 !py-2"
            onClick={() => navigate(`/${cluster_id}/make-payment`)}
          />
        </div>
      </div>
    );
  }

  const GetRow = (dataRow?: any) => {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={`Preview scheduled payment ${dataRow?.custom_schedule_id || dataRow?.id || ""}`}
        data-testid={tid(
          `scheduled-mobile-row-${dataRow?.custom_schedule_id || dataRow?.id || "unknown"}`
        )}
        className="w-full py-3 flex flex-col items-start justify-start gap-[10px] relative"
        onClick={() => {
          const custom_id = `${dataRow?.custom_schedule_id || ""}`;
          setIsPreview(true);
          setIsCustomSchPreviewId(dataRow?.id);
          setIsPreviewId(custom_id);
        }}
        onKeyDown={onActivate(() => {
          const custom_id = `${dataRow?.custom_schedule_id || ""}`;
          setIsPreview(true);
          setIsCustomSchPreviewId(dataRow?.id);
          setIsPreviewId(custom_id);
        })}
      >
        <div className="flex justify-between items-center w-full">
          <span className="text-xl font-medium leading-[26px] text-txtPrimary">
            {formatCurrency(dataRow?.amount + dataRow?.cf_amount) || "$0.00"}
          </span>
          <span className="flex items-center justify-end gap-2">
            <div
              className={`pointer-events-none flex items-center py-[3px] px-[10px] gap-[6px] rounded-md ${
                dataRow?.status?.toLowerCase() === "succeeded"
                  ? "bg-[#23B3390D] text-green text-sm"
                  : dataRow?.status?.toLowerCase() === "failed"
                    ? "bg-[#FF00000D] text-red text-sm"
                    : dataRow?.status?.toLowerCase() === "refunded"
                      ? "bg-[#1678F20D] text-blue text-sm"
                      : dataRow?.status?.toLowerCase() === "voided" ||
                          dataRow?.status?.toLowerCase() === "pending"
                        ? "bg-[#FDB6000D] text-yellow text-sm"
                        : dataRow?.status?.toLowerCase() === "partial refund"
                          ? "bg-[#F3F8FE] text-blue text-sm"
                          : dataRow?.status?.toLowerCase() === "disputed"
                            ? "bg-[#4F576A0D] text-green text-sm"
                            : ""
              }`}
            >
              {dataRow?.status?.toLowerCase() === "succeeded" ? (
                <GreenTrueIcon />
              ) : dataRow?.status?.toLowerCase() === "failed" ? (
                <RedCrossIcon />
              ) : dataRow?.status?.toLowerCase() === "refunded" ? (
                <MoneyReciveIcon />
              ) : dataRow?.status?.toLowerCase() === "voided" ? (
                <CloseYellowIcon />
              ) : dataRow?.status?.toLowerCase() === "partial refund" ? (
                <DollarProcessIcon />
              ) : dataRow?.status?.toLowerCase() === "disputed" ? (
                <InfoIcon />
              ) : dataRow?.status?.toLowerCase() === "pending" ? (
                <ClockYellowIcon />
              ) : (
                ""
              )}
              {dataRow?.status}
            </div>
            <Menu as="div" className="relative grid place-items-center">
              <Menu.Button
                disabled={isTableLoading}
                aria-label={`Open actions for scheduled payment ${dataRow?.id || ""}`}
                data-testid={tid(
                  `scheduled-mobile-row-${dataRow?.id || "unknown"}-actions-button`
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <div>
                  <MoreIcon
                    className="group"
                    _className="group-hover:!fill-iconObscure"
                  />
                </div>
              </Menu.Button>

              <Menu.Items className="absolute w-[153px] -left-[130px] top-[30px] !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg">
                <Menu.Item
                  as="div"
                  data-testid={tid(
                    `scheduled-mobile-row-${dataRow?.id || "unknown"}-preview-menu-item`
                  )}
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-t-[7px]"
                  onClick={() => {
                    setIsPreview(true);
                    setIsPreviewId(dataRow?.custom_schedule_id);
                    setIsCustomSchPreviewId(dataRow?.id);
                  }}
                >
                  <EyeIcon width="16" height="16" /> Preview
                </Menu.Item>
                {/* <Menu.Item
                  as="div"
                  data-testid={tid(
                    `scheduled-mobile-row-${dataRow?.id || "unknown"}-download-menu-item`
                  )}
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-b-[7px]"
                  onClick={() => onPaymentDownload(dataRow?.id)}
                >
                  {isDownloading === dataRow?.id ? (
                    <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
                  ) : (
                    <DownloadIcon width="16" height="16" />
                  )}{" "}
                  {isDownloading === dataRow?.id
                    ? "Downloading..."
                    : "Download"}
                </Menu.Item> */}
              </Menu.Items>
            </Menu>
          </span>
        </div>
        <div className="w-[85%] flex flex-col gap-[10px]">
          <div className="w-full flex gap-[10px] items-center text-txtParagraph text-sm leading-[18px]">
            <span className="w-[120px] shrink-0">Payment method</span>
            <span className="min-w-0 flex-1">
              {getPaymentMethod({
                card_type: dataRow?.cc_type,
                method: dataRow?.payment_method,
                digits: dataRow?.cc_4digit,
                account_number: dataRow?.cc_4digit,
                isHidden: false,
                bank_name: dataRow?.bank_name,
                rowId: dataRow?.id,
              })}
            </span>
          </div>
          <div className="w-full flex gap-[10px] items-center text-txtParagraph text-sm leading-[18px]">
            <span className="w-[120px] shrink-0">Date</span>
            <span className="min-w-0 flex-1 text-txtPrimary">
              {formatPaymentListDate(
                dataRow?.billing_date,
                userData?.data?.accountData?.timezone,
                "short"
              )}
            </span>
          </div>
          <div className="w-full flex gap-[10px] items-center text-txtParagraph text-sm leading-[18px]">
            <span className="w-[120px] shrink-0">Payment type</span>
            <span className="min-w-0 flex-1 text-txtPrimary">
              {dataRow.description || "--"}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="lg:px-10 lg:pt-10 h-full lg:overflow-y-auto"
      data-testid={tid("scheduled-tab")}
    >
      <div
        className="lg:px-0 px-5 flex items-center lg:gap-[28px] lg:flex-nowrap flex-wrap gap-5 lg:overflow-x-auto"
        data-testid={tid("scheduled-summary-filters")}
      >
        <div
          role="button"
          tabIndex={0}
          aria-pressed={selectedSlot === "all"}
          aria-label="Show all scheduled payments"
          data-testid={tid("scheduled-summary-filter-all")}
          className={`${
            selectedSlot === "all" ? "border-blue border-[1.5px]" : "border-strokePrimary border lg:border-[1.5px]"
          } p-2.5 sm:py-4 sm:pl-4 sm:pr-2 lg:p-4 rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[93px] lg:h-[100px] border-blue flex flex-col justify-between items-start lg:min-w-[200px]`}
          onClick={() => {
            setSelectedFilter("all");
          }}
          onKeyDown={onActivate(() => setSelectedFilter("all"))}
        >
          <div className="lg:text-2xl text-xl leading-[26px] lg:leading-normal font-medium text-txtPrimary whitespace-nowrap lg:whitespace-normal">
            All
          </div>
          <div className="text-lg leading-[23px] lg:leading-normal text-txtSecondary animate__animated animate__fadeIn">
            {isLoading ? (
              <Skeleton width={27} height={23} />
            ) : (
              scheduleHistoryCount?.AllMonthCount || 0
            )}
          </div>
        </div>
        <div
          role="button"
          tabIndex={0}
          aria-pressed={selectedSlot === "currentMonthCount"}
          aria-label="Show this month's scheduled payments"
          data-testid={tid("scheduled-summary-filter-this-month")}
          className={`${
            selectedSlot === "currentMonthCount"
              ? "border-blue border-[1.5px]"
              : "border-strokePrimary border lg:border-[1.5px]"
          } p-2.5 sm:py-4 sm:pl-4 sm:pr-2 lg:p-4 rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[93px] lg:h-[100px] border-blue flex flex-col justify-between items-start lg:min-w-[200px]`}
          onClick={() => {
            setSelectedFilter("currentMonthCount");
          }}
          onKeyDown={onActivate(() => setSelectedFilter("currentMonthCount"))}
        >
          <div className="lg:text-2xl text-xl leading-[26px] lg:leading-normal font-medium text-txtPrimary whitespace-nowrap lg:whitespace-normal">
            This Month
          </div>
          <div className="text-lg leading-[23px] lg:leading-normal text-txtSecondary animate__animated animate__fadeIn">
            {isLoading ? (
              <Skeleton width={27} height={23} />
            ) : (
              scheduleHistoryCount?.currentMonthCount || 0
            )}
          </div>
        </div>
        <div
          role="button"
          tabIndex={0}
          aria-pressed={selectedSlot === "nextMonthCount"}
          aria-label="Show next month's scheduled payments"
          data-testid={tid("scheduled-summary-filter-next-month")}
          className={`${
            selectedSlot === "nextMonthCount"
              ? "border-blue border-[1.5px]"
              : "border-strokePrimary border lg:border-[1.5px]"
          } p-2.5 sm:py-4 sm:pl-4 sm:pr-2 lg:p-4 rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[93px] lg:h-[100px] border-blue flex flex-col justify-between items-start lg:min-w-[200px]`}
          onClick={() => {
            setSelectedFilter("nextMonthCount");
          }}
          onKeyDown={onActivate(() => setSelectedFilter("nextMonthCount"))}
        >
          <div className="lg:text-2xl text-xl leading-[26px] lg:leading-normal font-medium text-txtPrimary whitespace-nowrap lg:whitespace-normal">
            Next Month
          </div>
          <div className="text-lg leading-[23px] lg:leading-normal text-txtSecondary animate__animated animate__fadeIn">
            {isLoading ? (
              <Skeleton width={27} height={23} />
            ) : (
              scheduleHistoryCount?.nextMonthCount || 0
            )}
          </div>
        </div>
        <div
          role="button"
          tabIndex={0}
          aria-pressed={selectedSlot === "next3Month"}
          aria-label="Show next three months of scheduled payments"
          data-testid={tid("scheduled-summary-filter-next-three-months")}
          className={`${
            selectedSlot === "next3Month"
              ? "border-blue border-[1.5px]"
              : "border-strokePrimary border lg:border-[1.5px]"
          } p-2.5 sm:py-4 sm:pl-4 sm:pr-2 lg:p-4 rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[93px] lg:h-[100px] border-blue flex flex-col justify-between items-start lg:min-w-[200px]`}
          onClick={() => {
            setSelectedFilter("next3Month");
          }}
          onKeyDown={onActivate(() => setSelectedFilter("next3Month"))}
        >
          <div className="lg:text-2xl text-xl leading-[26px] lg:leading-normal font-medium text-txtPrimary whitespace-nowrap lg:whitespace-normal">
            Next 3 Months
          </div>
          <div className="text-lg leading-[23px] lg:leading-normal text-txtSecondary animate__animated animate__fadeIn">
            {isLoading ? (
              <Skeleton width={27} height={23} />
            ) : (
              scheduleHistoryCount?.next3Month || 0
            )}
          </div>
        </div>
      </div>
      <div
        className="px-5 pt-[30px] flex flex-wrap items-center gap-2.5 lg:flex-nowrap lg:gap-3 lg:overflow-x-scroll lg:px-0 scrollbar-hide"
        ref={filterScrollRef}
        data-testid={tid("scheduled-filter-bar")}
      >
        <div className="w-[calc(50%-5px)] min-w-0 lg:w-fit" ref={dateFilterRef}>
          <DatePickerFilter
            dateState={dateState}
            timeRanges={scheduledTimeRanges}
            handleCallback={handleCallback}
            handleShow={handleShow}
            handleHide={handleHide}
            openCal={openCal}
            setOpenCal={setOpenCal}
            setDateState={setDateState}
            label={label}
            onOpen={() => {
              if (filterScrollRef.current && dateFilterRef.current) {
                filterScrollRef.current.scrollTo({
                  left: 0,
                  behavior: "smooth",
                });
              }
            }}
          />
        </div>
        <div
          className="w-[calc(50%-5px)] min-w-0 lg:w-fit"
          ref={amountFilterRef}
        >
          <AmountFilter
            options={FilterAmountOptions}
            amount={amount}
            setAmount={setAmount}
            label="Amount"
            onOpen={() => {
              if (filterScrollRef.current && amountFilterRef.current) {
                filterScrollRef.current.scrollTo({
                  left:
                    amountFilterRef.current.offsetLeft -
                    (filterScrollRef.current.offsetLeft || 0),
                  behavior: "smooth",
                });
              }
            }}
          />
        </div>
        <div
          className="w-[calc(50%-5px)] min-w-0 lg:w-fit"
          ref={methodFilterRef}
        >
          <SelectAllDropDown
            options={PaymentFilterMethodOptions}
            checked={method}
            setChecked={setMethod}
            label="Payment Method"
            onOpen={() => {
              if (filterScrollRef.current && methodFilterRef.current) {
                filterScrollRef.current.scrollTo({
                  left:
                    methodFilterRef.current.offsetLeft -
                    (filterScrollRef.current.offsetLeft || 0),
                  behavior: "smooth",
                });
              }
            }}
          />
        </div>
      </div>
      <div className="pt-[30px]">
        <CustomTable
          data-testid={tid("scheduled-table")}
          ariaLabel="Scheduled payments table"
          getRowKey={(row: ScheduledRow, index: number) =>
            row?.custom_schedule_id || row?.id || index
          }
          getRowTestId={(row: ScheduledRow, index: number) =>
            tid(
              `scheduled-mobile-row-wrapper-${
                row?.custom_schedule_id || row?.id || index
              }`
            )
          }
          columns={columns}
          data={scheduleHistory?.schedulePayment || []}
          fixedHeaderScrollHeight="calc(var(--shell-vh) - 452px)"
          perPage={filter?.limit}
          currentPage={(filter?.offset + 20) / 20}
          totalCount={scheduleHistory?.totalCount || 0}
          isTableLoading={isTableLoading}
          onNextClick={(e) => {
            setFilter({
              ...filter,
              offset: filter?.offset + 20,
            });
          }}
          onPreviousClick={(e) => {
            setFilter({
              ...filter,
              offset: filter?.offset - 20,
            });
          }}
          GetRow={GetRow}
          onRowClick={(row: any, e: any) => {
            setIsPreview(true);
            setIsPreviewId(row?.custom_schedule_id);
            setIsCustomSchPreviewId(row?.id);
          }}
        />
      </div>
      <div>
        <Drawer
          enableOverlay={true}
          lockBackgroundScroll={true}
          direction="right"
          open={isPreview}
          size={isLessThan590 ? `100vw` : "590px"}
          onClose={() => {
            setIsPreview(false);
            setIsPreviewId("");
            setIsCustomSchPreviewId("");
          }}
          overlayColor="#000"
        >
          <div
            className="contents"
            data-testid={tid("scheduled-preview-drawer")}
          >
            <PaymentPreview
              id={isPreviewId}
              setId={setIsPreviewId}
              show={isPreview}
              setShow={setIsPreview}
              onDownloadCLick={(id: any, receiptId?: any) =>
                onPaymentDownload(id, receiptId, true)
              }
              isScheduled={true}
              isDownloading={isDownloading?.toString()}
              custom_schedule_id={isCustomSchPreviewId}
            />
          </div>
        </Drawer>
      </div>
    </div>
  );
};

export default ScheduledTab;
